import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs.js';

export interface BreadcrumbItem {
  /** Visible label. */
  label: ReactNode;
  /** Optional href: if omitted, item renders as plain text (current page). */
  href?: string;
}

export interface BreadcrumbTrailProps extends BaseProps {
  items: BreadcrumbItem[];
  separator?: ReactNode;
  /**
   * Custom link renderer: useful for client-side routers (e.g. react-router's
   * `<Link>`). Receives `(href, label)` and returns a ReactNode. Defaults to a
   * plain `<a>` element so the component is router-agnostic.
   */
  renderLink?: (href: string, label: ReactNode) => ReactNode;
}

/**
 * Convenience wrapper around `<Breadcrumbs>` that accepts a typed `items`
 * array, handy when crumbs come from a routes map or are computed from the
 * URL. The last item (current page) renders as text even if `href` is set.
 *
 *   <BreadcrumbTrail
 *     items={[
 *       { label: 'Home',    href: '/' },
 *       { label: 'Tenants', href: '/tenants' },
 *       { label: 'Acme Corp' }
 *     ]}
 *     renderLink={(href, label) => <Link to={href}>{label}</Link>}
 *   />
 */
export const BreadcrumbTrail = forwardRef(function BreadcrumbTrail(
  { items, separator, renderLink, ...rest }: BreadcrumbTrailProps,
  ref: Ref<HTMLElement>
): ReactNode {
  const lastIndex = items.length - 1;
  return (
    <Breadcrumbs ref={ref} separator={separator} {...rest}>
      {items.map((item, i) => {
        const isLast = i === lastIndex;
        if (isLast || !item.href) {
          return <span key={i} aria-current={isLast ? 'page' : undefined}>{item.label}</span>;
        }
        if (renderLink) return <span key={i}>{renderLink(item.href, item.label)}</span>;
        return <a key={i} href={item.href}>{item.label}</a>;
      })}
    </Breadcrumbs>
  );
});
